import axios from 'axios';
import { get } from 'svelte/store';
import { derived } from 'svelte/store';
import { language } from '../../stores/languageStore.js';
import { config } from '../../../config/client.js';
import { translations } from './translations.js';

// Helper function to safely convert any value to a string
function safeStringify(value) {
    if (value === null || value === undefined) {
        return '';
    }
    
    if (typeof value === 'string') {
        return value;
    }
    
    try {
        // For objects, try to extract text content if it exists
        if (typeof value === 'object') {
            if (value.fullAnswer) {
                return typeof value.fullAnswer === 'string' ? value.fullAnswer : JSON.stringify(value.fullAnswer);
            }
            if (value.text) {
                return typeof value.text === 'string' ? value.text : JSON.stringify(value.text);
            }
        }
        
        // Otherwise stringify the object, but handle circular references
        const seen = new WeakSet();
        return JSON.stringify(value, (key, val) => {
            if (typeof val === 'object' && val !== null) {
                if (seen.has(val)) {
                    return '[Circular Reference]';
                }
                seen.add(val);
            }
            return val;
        });
    } catch (error) {
        console.error('Error stringifying value:', error);
        return String(value);
    }
}

export async function translateText(text, fromLang, toLang) {
    try {
        // If languages are the same, return the original text
        if (fromLang === toLang) {
            return text;
        }
        
        // Ensure text is a proper string
        const safeText = safeStringify(text);
        
        console.log('translateText called with:', { 
            originalText: typeof text === 'string' ? text : JSON.stringify(text), 
            textType: typeof text,
            safeText,
            fromLang, 
            toLang 
        });
        
        const payload = [{
            'text': safeText
        }];
        
        console.log('Translation payload:', JSON.stringify(payload));
        
        // Fix URL construction - ensure no double slashes
        const endpoint = config.azureTranslatorEndpoint.endsWith('/') 
            ? config.azureTranslatorEndpoint.slice(0, -1) 
            : config.azureTranslatorEndpoint;
        
        const response = await axios({
            baseURL: endpoint,
            url: '/translate',
            method: 'post',
            headers: {
                'Ocp-Apim-Subscription-Key': config.azureTranslatorKey,
                'Ocp-Apim-Subscription-Region': config.azureTranslatorRegion,
                'Content-type': 'application/json',
            },
            params: {
                'api-version': '3.0',
                'from': fromLang,
                'to': toLang
            },
            data: payload,
            responseType: 'json'
        });

        if (response.data && response.data[0] && response.data[0].translations && response.data[0].translations[0]) {
            return response.data[0].translations[0].text;
        } else {
            console.error('Unexpected translation response format:', response.data);
            return safeText; // Return original text if response format is unexpected
        }
    } catch (error) {
        console.error('Error in translation:', error);
        console.error('Error details:', {
            message: error.message,
            status: error.response?.status,
            statusText: error.response?.statusText,
            errorData: error.response?.data,
            requestData: error.config?.data
        });
        
        // Return the original text on error instead of throwing
        return typeof text === 'string' ? text : safeStringify(text);
    }
}

export async function translateToEnglish(text, sourceLanguage) {
    return await translateText(text, sourceLanguage, 'en');
}

export async function translateFromEnglish(text, targetLanguage) {
    // If text is an object with usageData, preserve the structure
    if (typeof text === 'object' && text !== null && text.usageData) {
        console.log('translate object:'+text);
        const translatedText = await translateText(text.fullAnswer || text.text || JSON.stringify(text), 'en', targetLanguage);
        return {
            fullAnswer: translatedText,
            usageData: text.usageData
        };
    }
    console.log('translate no object:'+text);
    return await translateText(text, 'en', targetLanguage);
}

export function getCurrentLanguage() {
    return get(language);
}

export const t = derived(
  language,
  $language => (key) => translations[$language][key] || key
);

export { translations };